'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";
import { Link } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import minimumViewport from 'minimum-viewport';
import ReactPaginate from 'react-paginate';

import {FaChevronRight, FaChevronLeft} from "react-icons/fa";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Table from "react-bootstrap/Table";

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States, Breakpoints} from '@uw-it-sis/lib-react/lib/AppConstants';
import Loading from '@uw-it-sis/lib-react/lib/Loading';
import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import Loader from '@uw-it-sis/lib-react/lib/Loader';

import Facet from '@uw-it-sis/lib-react-myplan/lib/Facet';
import FacetsPanel from '@uw-it-sis/lib-react-myplan/lib/FacetsPanel';
import FacetUtils from '@uw-it-sis/lib-react-myplan/lib/FacetUtils';
import FacetsModal from '@uw-it-sis/lib-react-myplan/lib/FacetsModal';
import GenEdUtils from '@uw-it-sis/lib-react-myplan/lib/GenEdUtils';

import courseSearchStore from './CourseSearchStore';
import courseSearchResultsStore from './CourseSearchResultsStore';

/**
 * Displays course search result entries.
 */
class CourseResults extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.analyticsStore = appStore.getAnalyticsStore();
    }

    addRankAnalytics(rank) {
        const additionalData = { "rankOrder": rank };
        this.analyticsStore.addEvent("Search-Rank-Order-Clicked-On-Result", {}, additionalData);
    }

    /*
     * Creates an array of term badge components.
     */
    makeCourseBadges(course, index) {
        let beginningBadge = null;
        let expiringBadge = null;

        // If beginningTermId is set create a updated/effective badge.
        if (course.hasOwnProperty("beginningTermId") && course.beginningTermId) {
            let beginningMessage = 'These course details are effective beginning ' + TermUtils.makeTermDescriptionFromTermId(course.beginningTermId, 'LONG');
            beginningBadge =
                <Badge key={1} bg="white" className="version-badge updated-version"
                       title={beginningMessage}>
                    <span aria-hidden={true}>{TermUtils.makeTermDescriptionFromTermId(course.beginningTermId, 'SHORT')}</span>
                    <span className="sr-only">{beginningMessage}</span>
                </Badge>;
        }

        // If expiringTermId is set create a updated/effective badge.
        if (course.hasOwnProperty("expiringTermId") && course.expiringTermId) {
            let expiringMessage = 'These course details are effective through ' + TermUtils.makeTermDescriptionFromTermId(course.expiringTermId, 'LONG');
            expiringBadge =
                <Badge key={2} bg="white" className="version-badge expiring-version"
                       title={expiringMessage}>
                    <span aria-hidden={true}>{TermUtils.makeTermDescriptionFromTermId(course.expiringTermId, 'SHORT')}</span>
                    <span className="sr-only">{expiringMessage}</span>
                </Badge>;
        }
        return [expiringBadge, beginningBadge];
    }

    render() {
        let isNotMobile = !this.props.isMobile;
        let rank = 0;

        const isDevMode = appStore.isDevMode();

        let courses = this.props.courses.map(function (course, index) {
            rank++;

            let courseSections = null;

            // Determine if this is a PCE section and choose the correct sectionFiltersObject.
            let sectionFilterObject = this.props.sectionFilters;
            let isPCE = course.institution === "UWPCE";
            if (isPCE) {
                sectionFilterObject = this.props.sectionFiltersPCE;
            }

            // Format the sectionFilter param for the CDP link if applicable. CDP doesn't mind if that param is missing.
            let sectionFiltersParam = "";
            if (sectionFilterObject) {
                sectionFiltersParam = sprintf('&sectionFilters=%s', encodeURIComponent(JSON.stringify(sectionFilterObject)));
            }

            if (course.sectionGroups.length) {
                let count = course.sectionGroups.length;
                let institute = isPCE ? " PCE" : "";
                courseSections = course.sectionGroups.length + institute + " section" + (count > 1 ? "s" : "");
            }

            let badgeWidgets;
            if (courseSearchStore.isSectionSearch()) {
                badgeWidgets = TermUtils.makeTermBadgeFromTermId(course.termId, index);
            } else {
                badgeWidgets = this.makeCourseBadges(course, index);
            }

            let courseLinkTitle = course.code + " " + course.title + (isDevMode ? " (" + sprintf("%.3f", course.score) + ")" : '');

            let courseCreditsText = course.credit + " " + (course.credit !== 1 ? "credits" : "credit");

            let courseGeneds;
            // Check for a single gen edu code for the special A&H, if code exists and only 1 code is present, then show help popover.
            if (course.genEduReqs[0] === 'sA&H' && course.genEduReqs.length === 1) {
                let genEdsUrl = "https://advising.uw.edu/counting-first-year-foreign-language-as-ah/";

                courseGeneds =
                    <HelpPopover title="Conditional A&H" link="*see note" placement="left" className="btn-inline-link">
                        1<sup>st</sup> year foreign language courses may, under certain conditions, count toward the A&amp;H component of your degree requirement. <a href={genEdsUrl} target="_blank">See website</a> or your adviser for more information.
                    </HelpPopover>;
            } else {
                courseGeneds = GenEdUtils.makeGenEdString(course.genEduReqs, GenEdUtils.DELIMITER.WORDS);
            }

            let coursePreReqs = null;
            if (course.prereqs.length > 0) {
                /* Capitalize the first letter of the prereq text */
                coursePreReqs =
                    <span className="course-prereqs">
                        Prerequisite: {course.prereqs.charAt(0).toUpperCase() + course.prereqs.slice(1)}
                    </span>;
            }

            if (isNotMobile) {
                // when not mobile, results are rendered in a table
                return (
                    <tr key={index}>
                        <th scope="row">
                            <Link title={courseLinkTitle}
                                  className="fw-bold d-block text-nowrap"
                                  onClick={this.addRankAnalytics.bind(this, rank)}
                                  to={{
                                      pathname: '/courses/' + course.code, search: "?id=" + course.courseId + sectionFiltersParam
                                  }}>
                                {course.code}
                            </Link>
                            <small className="mt-2 d-block text-nowrap text-muted">{courseSections}</small>
                        </th>
                        <td>
                            <Link title={courseLinkTitle}
                                  className="fw-bold d-block"
                                  onClick={this.addRankAnalytics.bind(this, rank)}
                                  to={{
                                      pathname: '/courses/' + course.code, search: "?id=" + course.courseId + sectionFiltersParam
                                  }}>
                                {course.title}
                            </Link>
                            <small className="mt-2 d-block text-muted"><span className="sr-only">Course</span> {coursePreReqs}</small>
                        </td>
                        <td>
                            {badgeWidgets}
                            {courseSearchStore.isSectionSearch() ? <span className="sr-only">{courseSections} offered</span> : null}
                        </td>
                        <td>
                            <span aria-hidden={true}>{course.credit}</span>
                            <span className="sr-only">{courseCreditsText}</span>
                        </td>
                        <td>
                            {courseGeneds}
                        </td>
                    </tr>
                );
            } else {
                // when mobile, render results as list items with flex display
                return (
                    <li className="list-group-item" key={index}>
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1 text-truncate pe-3">
                                <Link
                                    title={courseLinkTitle}
                                    className="fw-bold"
                                    onClick={this.addRankAnalytics.bind(this, rank)}
                                    to={{
                                        pathname: '/courses/' + course.code, search: "?id=" + course.courseId + sectionFiltersParam
                                    }}>
                                    <span className="me-3">{course.code}</span>
                                    <span>{course.title}</span>
                                </Link>
                            </div>
                            <div>
                                <span className="d-inline d-md-none" aria-hidden="true">({course.credit})</span>
                                <span className="sr-only">{courseCreditsText}</span>
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <div className="pe-3">
                                {badgeWidgets}
                            </div>
                            <div>
                                {courseSections}
                            </div>
                            <div className="flex-grow-1 text-truncate ps-3 text-end">
                                {courseGeneds}
                            </div>
                        </div>
                    </li>
                );
            }
        }, this);

        if (isNotMobile) {
            // when not mobile, render results as a table
            return (
                <Table borderless striped>
                    <colgroup>
                        <col span={1} width="13%" />
                        <col span={1} width="45%" />
                        <col span={1} width="15%" />
                        <col span={1} width="9%" />
                        <col span={1} width="18%" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col">Course Code</th>
                            <th scope="col">Course Title</th>
                            <th scope="col">{courseSearchStore.isSectionSearch() ? 'Quarter Offered':'Effective'}</th>
                            <th scope="col">Credits</th>
                            <th scope="col"><abbr title="General Education Requirements">Gen Ed Req</abbr></th>
                        </tr>
                    </thead>
                    <tbody>
                        {courses}
                    </tbody>
                </Table>
            );
        } else {
            return (
                <ul className="list-group list-group-striped mb-0">
                    {courses}
                </ul>
            );
        }
    }
}

CourseResults.propTypes = {
    courses: PropTypes.array.isRequired,
    sectionFilters: PropTypes.object,
    sectionFiltersPCE: PropTypes.object,
    isMobile: PropTypes.bool
};

/**
 * Displays course search results.
 */
class CourseSearchResultsPanel extends Component {

    constructor(props) {
        super(props);
        this.props = props;

        this.store = courseSearchResultsStore;

        this.width = window.innerWidth;

        this.onChangeSort = this.onChangeSort.bind(this);
        this.onPageChange = this.onPageChange.bind(this);
        this.onHandleFilterChange = this.onHandleFilterChange.bind(this);
        this.onHandleResetFacet = this.onHandleResetFacet.bind(this);
        this.onClickResetFilters = this.onClickResetFilters.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    /*
     * Initialize the store.
     */
    componentDidMount() {
        this.store.mount(this.props.courses, this.props.newSearch);
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
        let newWidth = window.innerWidth;
        let mdWidth = Breakpoints.md.width;

        if (
            (newWidth < mdWidth && this.width >= mdWidth)
            ||
            (newWidth >= mdWidth && this.width < mdWidth)
        ) {
            this.width = newWidth;
            this.store.mount(this.props.courses, this.props.newSearch);
        }
    }

    /**
     * Handler for filter widget change.
     */
    onHandleFilterChange = (obj) => {
        // Dig out the filter Id(s) here. Because Synthetic events are reused the props will get nulled if we wait until later.
        const filterIds = FacetUtils.findFilterIds(obj);
        this.store.toggleFacetFilters(filterIds);
    };

    /**
     * Handler for individual facet reset.
     */
    onHandleResetFacet = (e) => {
        /*
         * This has to be done now because the event objects are reused and some of the properties get nulled
         * if you wait.
         */
        const facetId = FacetUtils.findFacetId(e);
        this.store.resetFacet(facetId);
    };

    /**
     * Handler for the "reset all filters" click.
     */
    onClickResetFilters = () => this.store.resetAllFacets();

    /**
     * Handle sort widget changes.
     */
    onChangeSort = (e) => {
        const value = e.target.value;
        const displayValue = e.target.options[e.target.options.selectedIndex].text;
        this.store.sortResults(value, displayValue);
    };

    /**
     * Handle pagination.
     * @param pageNumber The new page number.
     */
    onPageChange = (event) => {
        let pageNumber = event.selected;
        // This is used to scroll the view back to the top of the results after a page change.
        const searchForm = document.getElementById("course-search-form");
        const y = searchForm.getBoundingClientRect().bottom + window.scrollY;
        window.scrollTo({top: y});
        this.store.pageChange(pageNumber);
    };

    render() {
        if (this.store.state !== States.done) {
            return (
                <Loading type="spinner2" alt={this.store.actionPendingText} />
            );
        }

        // Create a list of facets from the facet models.

        let facetModels = this.store.getFacetModels();
        let facets = facetModels.map(function (facetModel, index) {
            if (Object.keys(facetModel.getFilterModels()).length > 1) {
                return (
                    <Facet key={index} facetModel={facetModel}
                           handleFilterChange={this.onHandleFilterChange}
                           handleResetFacet={this.onHandleResetFacet}/>
                );
            }
        }, this);

        let panel;
        if (this.store.getFilteredSearchResultsCount() > 0) {
            /*
             * Because on the CDP we want to make the PCE tab active if a PCE search result is clicked we need to create
             * a PCE and non-PCE version of the section filters object. Another approach would be to pass the method down
             * to CourseResults and let it call for the objects, but this approach lets us optimize easier (fewer calls,
             * no caching necessary, etc)
             */
            let sectionFilters = this.props.sectionFiltersMethod(false);
            let sectionFiltersPCE = this.props.sectionFiltersMethod(true);

            panel =
                <div className="search-results">
                    <CourseResults
                        courses={this.store.getFilteredSearchResults(this.props.resultsPerPage)}
                        sectionFilters={sectionFilters}
                        sectionFiltersPCE={sectionFiltersPCE}
                        isMobile={this.width < Breakpoints.md.width} />
                </div>;
        } else {
            panel =
                <div className="courseSearch-noResults">
                    <h3>Looks like you filtered away all of your search results</h3>
                    <p>A few suggestions:</p>
                    <ul>
                        <li>Perform the inverse of the last filtering action you performed :)</li>
                        <li>Click the 'clear' item at the top of the last filter that you updated</li>
                        <li><a onClick={this.onClickResetFilters}>Clear all filters</a></li>
                    </ul>
                </div>;
        }

        const paginateCssClasses = {
            element: "page-item flex-fill",
            link: "page-link text-center"
        }

        let pagination;
        if ( Math.ceil(this.store.getFilteredSearchResultsCount() / this.props.resultsPerPage) > 1 ) {
            pagination =
                <Row>
                    <Col xs={12} sm={8} className="mx-auto" role="navigation" aria-label="Search Result Navigation">
                        <ReactPaginate
                            previousLabel={<FaChevronLeft aria-hidden={true} />}
                            nextLabel={<FaChevronRight aria-hidden={true} />}
                            pageCount={Math.ceil(this.store.getFilteredSearchResultsCount() / this.props.resultsPerPage)}
                            onPageChange={this.onPageChange}
                            containerClassName="pagination"
                            previousClassName={paginateCssClasses.element}
                            pageClassName={paginateCssClasses.element}
                            nextClassName={paginateCssClasses.element}
                            previousLinkClassName={paginateCssClasses.link}
                            pageLinkClassName={paginateCssClasses.link}
                            nextLinkClassName={paginateCssClasses.link}
                            pageLabelBuilder={(page) => `Page ${page}`}
                            onPageActive={() => false} // do nothing when active link clicked
                            activeLinkClassName="btn-no-hover btn-no-outline"
                            initialPage={this.store.data.paginationActivePage}
                            marginPagesDisplayed={0}
                            pageRangeDisplayed={0}
                        />
                    </Col>
                </Row>
        }

        return (
            <div id="course-search-results-panel">
                <Loader show={(this.store.state === States.pending && ! minimumViewport({x: 992}))} message={this.store.actionPendingText}>
                    <Row>
                        <Col xs={12} className="mt-3 mb-3">
                            <Row>
                                <Col>
                                    <h2 className="h4 fw-bold mb-0">{this.store.getFilteredSearchResultsPaginationRange(this.props.resultsPerPage)} for '{this.props.queryString}'</h2>
                                </Col>
                                <Col xs="auto" className="d-lg-none">
                                    <FacetsModal facets={facets} loaderMessage={this.store.actionPendingText}
                                                 showLoader={(this.store.state === States.pending && minimumViewport({x: 991}))}/>
                                </Col>
                            </Row>
                        </Col>
                        <Col lg={3} className="d-none d-lg-block">
                            <FacetsPanel facets={facets} headerText={<h3>Filters</h3>} />
                        </Col>
                        <Col xs={12} lg={9}>
                            <Card className="card-panel" role="region" aria-label={`Search Results for '${this.props.queryString}' - Viewing ${this.store.getFilteredSearchResultsPaginationRange(this.props.resultsPerPage)}`}>
                                <Card.Header className="d-flex align-items-center justify-content-between">
                                    <h3>Courses</h3>
                                    <Form.Group className="d-flex align-items-center d-print-none w-auto">
                                        <Form.Label id="course-search-sort-label"
                                                    className="fs-6 text-nowrap mb-0 me-2 text-capitalize">Sort by:</Form.Label>
                                        <Form.Control as="select" name="sortMode"
                                                      onChange={this.onChangeSort}
                                                      value={this.store.data.sortMode}
                                                      id="search-results-sort"
                                                      className="bg-transparent border-0 shadow-none"
                                                      aria-labelledby="course-search-sort-label"
                                                      dir="rtl">
                                            <option value="relevance">Relevance</option>
                                            <option value="courseCode">Course Code</option>
                                            <option value="courseTitle">Course Title</option>
                                        </Form.Control>
                                    </Form.Group>
                                </Card.Header>
                                <Card.Body>
                                    {panel}
                                </Card.Body>
                            </Card>
                            {pagination}
                        </Col>
                    </Row>
                </Loader>
            </div>
        );
    }
}

CourseSearchResultsPanel.propTypes = {
    courses: PropTypes.array.isRequired,
    newSearch: PropTypes.bool.isRequired,
    queryString: PropTypes.string.isRequired,
    resultsPerPage: PropTypes.number.isRequired,
    sectionFiltersMethod: PropTypes.func.isRequired
};

export default observer(CourseSearchResultsPanel);